// src/screens/DocumentosScreen.js
import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, doc, addDoc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import {
  IoFolderOpenOutline,
  IoSearchOutline,
  IoClose,
  IoAddCircleOutline,
  IoTrashOutline,
  IoEyeOutline,
  IoDownloadOutline,
  IoShareSocialOutline,
  IoDocumentTextOutline,
  IoImageOutline,
  IoDocumentOutline,
  IoCloudUploadOutline
} from 'react-icons/io5';
import moment from 'moment';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import './DocumentosScreen.css';

const CATEGORIES = [
  { id: 'todos', label: 'Todos' },
  { id: 'reglamento', label: '📜 Reglamento' },
  { id: 'actas', label: '✍️ Actas' },
  { id: 'planos', label: '📐 Planos' },
  { id: 'estetica', label: '🎨 Estética' },
  { id: 'finanzas', label: '📊 Finanzas / Transparencia' },
  { id: 'otros', label: '📁 Otros' }
];

function DocumentosScreen() {
  const { currentUser, userData } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [financialReports, setFinancialReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('todos');

  // Modal States
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docTitle, setDocTitle] = useState('');
  const [docDesc, setDocDesc] = useState('');
  const [docCategory, setDocCategory] = useState('reglamento');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Edit State
  const [editingDocId, setEditingDocId] = useState(null);

  // Custom Alert Popups
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    title: '',
    message: '',
    type: 'success', // success | error | confirm
    onConfirm: null
  });

  const showAlert = (title, message, type = 'success', onConfirm = null) => {
    setAlertConfig({
      show: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const closeAlert = () => {
    setAlertConfig(prev => ({ ...prev, show: false }));
  };

  // Role Checks
  const userRole = userData?.rol || 'residente';
  const canUpload = userRole === 'admin' || userRole === 'comite' || userRole === 'masteruser';
  const canEditOrDelete = userRole === 'comite' || userRole === 'masteruser';

  useEffect(() => {
    if (!currentUser?.uid || !userData?.condominioId) return;

    // 1. Fetch Official Documents
    const q = query(
      collection(db, 'documentos_oficiales'),
      where('condominioId', '==', userData.condominioId),
      orderBy('fechaCreacion', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDocuments(docsList);
    }, (error) => {
      console.error('Error fetching documents:', error);
    });

    // 2. Fetch Financial Reports
    const qFin = query(
      collection(db, "reportesFinancieros"),
      where("condominioId", "==", userData.condominioId),
      orderBy("anio", "desc"),
      orderBy("mes", "desc")
    );

    const unsubscribeFin = onSnapshot(qFin, (snapshot) => {
      const finList = snapshot.docs.map(doc => {
        const data = doc.data();
        const getNombreMes = (numMes) => {
          return moment(numMes, 'M').format('MMMM');
        };
        return {
          id: doc.id,
          ...data,
          categoria: 'finanzas',
          fileType: 'pdf',
          fileSize: 'PDF',
          descripcion: `Reporte Financiero de ${getNombreMes(data.mes)} ${data.anio}`,
          fileUrl: data.archivoUrl,
          fechaCreacion: data.fechaCreacion || null
        };
      });
      setFinancialReports(finList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching financial reports:', error);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      unsubscribeFin();
    };
  }, [currentUser, userData]);

  const handleSelectFile = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      if (!docTitle) {
        // Fallback title to clean filename
        const cleanName = e.target.files[0].name.substring(0, e.target.files[0].name.lastIndexOf('.')) || e.target.files[0].name;
        setDocTitle(cleanName);
      }
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!docTitle.trim() || !selectedFile) {
      showAlert('Faltan Datos', 'Por favor completa el título y selecciona un archivo.', 'error');
      return;
    }

    setUploading(true);
    try {
      const timestamp = Date.now();
      const storagePath = `documentos_condominio/${userData.condominioId}/${timestamp}_${selectedFile.name}`;
      const storageRef = ref(storage, storagePath);

      // Upload file to storage
      await uploadBytes(storageRef, selectedFile);
      const fileUrl = await getDownloadURL(storageRef);

      // Get clean properties
      const extension = selectedFile.name.split('.').pop().toLowerCase();
      let fileType = 'default';
      if (['pdf'].includes(extension)) fileType = 'pdf';
      else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) fileType = 'image';
      else if (['doc', 'docx'].includes(extension)) fileType = 'word';

      const sizeInMB = (selectedFile.size / (1024 * 1024)).toFixed(2) + ' MB';

      // Save record in Firestore
      await addDoc(collection(db, 'documentos_oficiales'), {
        condominioId: userData.condominioId,
        titulo: docTitle,
        descripcion: docDesc,
        categoria: docCategory,
        fileUrl,
        fileType,
        fileName: selectedFile.name,
        fileSize: sizeInMB,
        storagePath,
        fechaCreacion: serverTimestamp(),
        creadoPor: currentUser.uid
      });

      showAlert('¡Éxito!', 'Documento publicado correctamente.', 'success');
      setShowUploadModal(false);
      resetForm();
    } catch (error) {
      console.error('Error uploading document:', error);
      showAlert('Error de Publicación', 'No tienes permisos suficientes o el archivo no es válido: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!docTitle.trim()) return;

    setUploading(true);
    try {
      const docRef = doc(db, 'documentos_oficiales', editingDocId);
      await updateDoc(docRef, {
        titulo: docTitle,
        descripcion: docDesc,
        categoria: docCategory
      });

      showAlert('Actualizado', 'Información del documento modificada con éxito.', 'success');
      setShowUploadModal(false);
      resetForm();
    } catch (e) {
      console.error(e);
      showAlert('Error', 'No tienes permisos para modificar este documento.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (documentItem, e) => {
    e.stopPropagation();
    setEditingDocId(documentItem.id);
    setDocTitle(documentItem.titulo);
    setDocDesc(documentItem.descripcion || '');
    setDocCategory(documentItem.categoria);
    setSelectedFile({ name: documentItem.fileName, mock: true });
    setShowUploadModal(true);
  };

  const handleDelete = (documentItem, e) => {
    e.stopPropagation();
    showAlert(
      '¿Eliminar Documento?',
      `¿Estás seguro de eliminar permanentemente "${documentItem.titulo}"? Esta acción no se puede deshacer.`,
      'confirm',
      async () => {
        try {
          // 1. Delete from storage if storagePath exists
          if (documentItem.storagePath) {
            const fileRef = ref(storage, documentItem.storagePath);
            await deleteObject(fileRef).catch(err => console.warn('File not found in storage or deleted already', err));
          }
          // 2. Delete from firestore
          await deleteDoc(doc(db, 'documentos_oficiales', documentItem.id));
          showAlert('Eliminado', 'El documento ha sido removido exitosamente.', 'success');
        } catch (error) {
          console.error('Error deleting document:', error);
          showAlert('Error al eliminar', 'No tienes permisos para eliminar este documento.', 'error');
        }
      }
    );
  };

  const resetForm = () => {
    setDocTitle('');
    setDocDesc('');
    setDocCategory('reglamento');
    setSelectedFile(null);
    setEditingDocId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleShare = async (documentItem, e) => {
    e.stopPropagation();
    if (Capacitor.isNativePlatform()) {
      try {
        await Share.share({
          title: documentItem.titulo,
          text: documentItem.descripcion,
          url: documentItem.fileUrl,
          dialogTitle: 'Compartir Documento'
        });
      } catch (err) {
        console.error(err);
      }
    } else {
      navigator.clipboard.writeText(documentItem.fileUrl);
      showAlert('Enlace Copiado', 'El enlace de descarga se copió al portapapeles.', 'success');
    }
  };

  // Filtered list
  const filteredDocuments = [...documents, ...financialReports].filter(docItem => {
    const matchesSearch = docItem.titulo.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (docItem.descripcion || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'todos' || docItem.categoria === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const getDocIcon = (type) => {
    switch (type) {
      case 'pdf': return <IoDocumentTextOutline />;
      case 'image': return <IoImageOutline />;
      default: return <IoDocumentOutline />;
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/documentos" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>
        <div className="ks-scroll-area">
          <div className="documentos-header-stitch">
            <div className="muro-hero-card" style={{ background: 'linear-gradient(135deg, #7B61FF 0%, #6348D1 100%)', boxShadow: '0 12px 32px rgba(99, 72, 209, 0.28)' }}>
              <div className="muro-hero-content">
                <h1>Biblioteca Vecinal</h1>
                <p>Reglamentos, Actas de Asamblea y Documentos Oficiales</p>
                {canUpload && (
                  <button
                    className="muro-hero-action-btn"
                    style={{ color: '#6348D1', marginTop: '16px' }}
                    onClick={() => { resetForm(); setShowUploadModal(true); }}
                  >
                    <IoAddCircleOutline size={20} /> Subir Documento
                  </button>
                )}
              </div>
              <IoFolderOpenOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div className="documentos-dashboard-body">
            {/* Search Bar */}
            <div className="muro-search-wrapper" style={{ marginTop: '10px', marginBottom: '16px' }}>
              <IoSearchOutline className="muro-search-icon" />
              <input
                type="text"
                placeholder="Buscar reglamento, actas, planos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="muro-search-input"
              />
            </div>

            {/* Categories */}
            <div className="documentos-category-tags">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`category-tag ${activeCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                  style={activeCategory === cat.id ? { background: '#0284c7' } : {}}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Document grid */}
            {loading ? (
              <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px' }}>Cargando documentos...</p>
            ) : filteredDocuments.length === 0 ? (
              <div className="empty-state" style={{ padding: '60px 20px' }}>
                <IoFolderOpenOutline size={50} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
                <h3>No hay documentos</h3>
                <p>Prueba con otra categoría o término de búsqueda.</p>
              </div>
            ) : (
              <div className="documents-grid">
                {filteredDocuments.map(docItem => (
                  <div key={docItem.id} className="document-card">
                    {/* Committee controls */}
                    {canEditOrDelete && docItem.categoria !== 'finanzas' && (
                      <div style={{ position: 'absolute', top: '15px', right: '15px', display: 'flex', gap: '5px' }}>
                        <button className="document-admin-btn" onClick={(e) => startEdit(docItem, e)} title="Editar información">
                          Editar
                        </button>
                        <button className="document-admin-btn delete" onClick={(e) => handleDelete(docItem, e)} title="Eliminar archivo permanentemente">
                          <IoTrashOutline size={16} />
                        </button>
                      </div>
                    )}

                    <div className="document-card-header">
                      <div className={`document-icon-box ${docItem.fileType || 'default'}`}>
                        {getDocIcon(docItem.fileType)}
                      </div>
                      <div className="document-title-wrap" style={{ marginRight: (canEditOrDelete && docItem.categoria !== 'finanzas') ? '65px' : '0' }}>
                        <h4 className="document-title">{docItem.titulo}</h4>
                        <span className="document-meta-line">
                          {docItem.fileSize || 'N/A'} • {docItem.fechaCreacion && typeof docItem.fechaCreacion.toDate === 'function' ? moment(docItem.fechaCreacion.toDate()).format('DD/MM/YYYY') : 'Reciente'}
                        </span>
                      </div>
                    </div>

                    <p className="document-desc">{docItem.descripcion || 'Sin descripción'}</p>

                    <div className="document-actions">
                      <a
                        href={docItem.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="document-action-btn"
                        style={{ textDecoration: 'none' }}
                      >
                        <IoEyeOutline size={18} /> Visualizar
                      </a>
                      <button className="document-action-btn" onClick={(e) => handleShare(docItem, e)}>
                        <IoShareSocialOutline size={16} /> Compartir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upload/Edit Modal */}
        {showUploadModal && (
          <div className="premium-modal-overlay">
            <div className="premium-modal" style={{ maxWidth: '480px' }}>
              <div className="modal-header">
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
                  {editingDocId ? 'Editar Información' : 'Publicar Documento'}
                </h2>
                <button className="modal-close-btn" onClick={() => setShowUploadModal(false)}>
                  <IoClose />
                </button>
              </div>

              <form onSubmit={editingDocId ? handleEditSubmit : handleUploadSubmit}>
                <div className="doc-form-group">
                  <label className="doc-form-label">Título del Documento</label>
                  <input
                    type="text"
                    required
                    value={docTitle}
                    onChange={(e) => setDocTitle(e.target.value)}
                    className="doc-form-input"
                    placeholder="Ej: Reglamento Interno de Convivencia"
                  />
                </div>

                <div className="doc-form-group">
                  <label className="doc-form-label">Descripción / Notas</label>
                  <textarea
                    rows="3"
                    value={docDesc}
                    onChange={(e) => setDocDesc(e.target.value)}
                    className="doc-form-textarea"
                    placeholder="Agrega notas aclaratorias o fecha de aprobación..."
                  />
                </div>

                <div className="doc-form-group">
                  <label className="doc-form-label">Categoría del Archivo</label>
                  <select
                    value={docCategory}
                    onChange={(e) => setDocCategory(e.target.value)}
                    className="doc-form-select"
                  >
                    <option value="reglamento">📜 Reglamento</option>
                    <option value="actas">✍️ Actas de Asamblea</option>
                    <option value="planos">📐 Planos del Condominio</option>
                    <option value="estetica">🎨 Estética y Diseño</option>
                    <option value="otros">📁 Otros</option>
                  </select>
                </div>

                {/* File picker */}
                {!editingDocId && (
                  <div className="doc-form-group">
                    <label className="doc-form-label">Seleccionar Archivo (PDF, Imagen, Word)</label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleSelectFile}
                      style={{ display: 'none' }}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    />
                    {selectedFile ? (
                      <div className="selected-file-badge">
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '250px' }}>
                          📄 {selectedFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedFile(null)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Remover
                        </button>
                      </div>
                    ) : (
                      <div className="file-dropzone" onClick={() => fileInputRef.current.click()}>
                        <IoCloudUploadOutline className="file-dropzone-icon" />
                        <div className="file-dropzone-text">Arrastra o haz clic para seleccionar archivo</div>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    className="document-action-btn"
                    onClick={() => setShowUploadModal(false)}
                    style={{ flex: 1 }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="pay-btn-primary"
                    style={{ flex: 1, background: '#0284c7', borderColor: '#0284c7', color: 'white', display: 'flex', justifyContent: 'center' }}
                  >
                    {uploading ? 'Procesando...' : editingDocId ? 'Guardar' : 'Subir'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Custom Alert Popups */}
        {alertConfig.show && (
          <div className="premium-modal-overlay" style={{ zIndex: 11000 }}>
            <div className="premium-modal" style={{ maxWidth: '380px', textAlign: 'center', padding: '30px 24px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: alertConfig.type === 'success' ? '#ecfdf5' : alertConfig.type === 'error' ? '#fee2e2' : '#fef3c7',
                color: alertConfig.type === 'success' ? '#10b981' : alertConfig.type === 'error' ? '#ef4444' : '#f59e0b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                margin: '0 auto 16px auto',
                fontWeight: 'bold'
              }}>
                {alertConfig.type === 'success' ? '✓' : alertConfig.type === 'error' ? '✕' : '⚠'}
              </div>
              
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: '#111827', fontWeight: '800' }}>
                {alertConfig.title}
              </h3>
              
              <p style={{ margin: '0 0 24px 0', fontSize: '0.88rem', color: '#6b7280', lineHeight: '1.4' }}>
                {alertConfig.message}
              </p>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                {alertConfig.type === 'confirm' && (
                  <button
                    type="button"
                    className="document-action-btn"
                    onClick={closeAlert}
                    style={{ flex: 1 }}
                  >
                    Cancelar
                  </button>
                )}
                <button
                  type="button"
                  className="pay-btn-primary"
                  onClick={() => {
                    if (alertConfig.onConfirm) {
                      alertConfig.onConfirm();
                    }
                    closeAlert();
                  }}
                  style={{
                    flex: 1,
                    background: alertConfig.type === 'error' ? '#ef4444' : alertConfig.type === 'success' ? '#10b981' : '#0284c7',
                    borderColor: alertConfig.type === 'error' ? '#ef4444' : alertConfig.type === 'success' ? '#10b981' : '#0284c7',
                    color: 'white',
                    justifyContent: 'center'
                  }}
                >
                  {alertConfig.type === 'confirm' ? 'Confirmar' : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        )}

        <KondoBottomNav activeRoute="/documentos" />
      </main>
    </div>
  );
}

export default DocumentosScreen;
